'use client';

import { useFavoritesQuery } from "@/queries/favorites/useFavoritesQuery";
import BookmarkSvgIcon from "@/shared/icons/BookmarkSvgIcon";
import { closeDrawer } from "@/shared/store/drawer";
import { setFavoriteItems } from "@/shared/store/shop";
import { Box, BoxProps, HStack, Icon, Skeleton, SkeletonText, Stack, Text } from "@chakra-ui/react";
import { useTranslations } from "next-intl";
import { forwardRef, useEffect } from "react";
import { LinkButton } from "../ui/link-button";
import FavoriteItemList from "./FavoriteItemList";

type Props = {
} & BoxProps;

const Favorites = forwardRef<HTMLDivElement, Props>(({ ...boxProps }, ref) => {
  const t = useTranslations();
  const { data, isLoading } = useFavoritesQuery();

  const FavoritesSkeleton = () => (
    <Stack gap={6} maxW="xs" w="100%">
      {[1, 2, 3].map(i => (
        <HStack key={i} width="full" align="start" mb={4}>
          <Skeleton height="150px" width="120px" borderRadius="8px" />
          <Stack flex="1" gap={2}>
            <SkeletonText noOfLines={2} gap={2} height="16px" width="80%" />
            <Skeleton height="20px" width="60px" />
            <Skeleton height="36px" width="100px" mt={4} />
          </Stack>
        </HStack>
      ))}
    </Stack>
  );

  useEffect(() => {
    if (data) setFavoriteItems(data.items);
  }, [data]);

  if (isLoading) return <FavoritesSkeleton />;

  if (!data || !data.items.length) return (
    <Stack gap="16px">
      <Stack gap="8px">
        <Icon
          boxSize={6}
          fill={"none"}
          stroke={"black"}
          _groupHover={{
            stroke: "accent",
            fill: "accent.secondary",
          }}>
          <BookmarkSvgIcon />
        </Icon>
        <Text textStyle="bold16" lineHeight="28px">{t('favorites_empty')}</Text>
        <Text textStyle="regular14" lineHeight="28px" color="gray.600">{t('favorites_empty_text')}</Text>
      </Stack>
      <LinkButton variant={"qr_secondary"} minH={"40px"} href="/shop" onClick={() => closeDrawer()}>
        {t('go_to_shopping')}
      </LinkButton>
    </Stack>
  );

  return (
    <Box
      ref={ref}
      className="favorites"
      {...boxProps}
    >
      <FavoriteItemList items={data.items} />
    </Box>
  );
});

Favorites.displayName = "Cart";
export default Favorites;